import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./SupervisioneDipendenti.css";
import { database, timestamp } from "../../firebase/config";
import { AdminOnly } from "../../components/RoleBasedAccess";
import { useCollection } from "../../hooks/useCollection";
import { useAuthContext } from "../../hooks/useAuthContext";
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
} from "date-fns";
import Select from "react-select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { it as itLocale } from "date-fns/locale";

const DEFAULT_GCAL_EMBED_URL =
  "https://calendar.google.com/calendar/embed?src=dr6u7dplrgs40707jsgung4dug%40group.calendar.google.com&ctz=Europe%2FRome";

const TASK_STATUS_OPEN = "open";
const TASK_STATUS_DONE = "done";

const SELECT_MENU_PORTAL_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const createTaskObject = (descrizione = "", status = TASK_STATUS_OPEN) => ({
  descrizione: String(descrizione || "").trim(),
  status: status === TASK_STATUS_DONE ? TASK_STATUS_DONE : TASK_STATUS_OPEN,
});

const normalizeActivityTasks = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((task) => {
        if (typeof task === "string") {
          return createTaskObject(task, TASK_STATUS_OPEN);
        }

        if (task && typeof task === "object") {
          const descrizione = String(
            task.descrizione || task.label || task.title || task.text || "",
          ).trim();
          if (!descrizione) return null;
          return createTaskObject(descrizione, task.status);
        }

        return null;
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((task) => createTaskObject(task, TASK_STATUS_OPEN))
      .filter((task) => task.descrizione);
  }

  return [];
};

const formatTasksInline = (tasks) =>
  normalizeActivityTasks(tasks)
    .map((task) => task.descrizione)
    .filter(Boolean)
    .join(" • ");

const sanitizeOptionalText = (value) => {
  const nextValue = String(value || "").trim();
  return nextValue || null;
};

const getUserDisplayName = (userDoc) =>
  userDoc?.anagrafica
    ? `${userDoc.anagrafica.nome} ${userDoc.anagrafica.cognome}`
    : userDoc?.displayName || userDoc?.id || "—";

const getProjectDisplayLabel = (project) =>
  project?.nome || project?.codice || project?.id || "—";

const getVehicleDisplayLabel = (mezzo) =>
  [mezzo?.modello, mezzo?.targa].filter(Boolean).join(" ") ||
  mezzo?.targa ||
  mezzo?.modello ||
  "—";

const isAssignmentAbsence = (assignment) =>
  assignment?.status === "ok" || assignment?.status === "assenza";

function useTaskPlannerState() {
  const [tasks, setTasks] = useState([]);
  const [taskDraft, setTaskDraft] = useState("");

  const normalizedTasks = useMemo(() => normalizeActivityTasks(tasks), [tasks]);

  const completedTasksCount = useMemo(
    () =>
      normalizedTasks.filter((task) => task.status === TASK_STATUS_DONE).length,
    [normalizedTasks],
  );

  const addTask = useCallback(
    (nextValue) => {
      const taskLabel = String(nextValue ?? taskDraft).trim();
      if (!taskLabel) return;
      setTasks((prev) => [
        ...prev,
        createTaskObject(taskLabel, TASK_STATUS_OPEN),
      ]);
      setTaskDraft("");
    },
    [taskDraft],
  );

  const updateTask = useCallback((index, value) => {
    setTasks((prev) =>
      prev.map((task, currentIndex) =>
        currentIndex === index
          ? createTaskObject(value, task?.status)
          : createTaskObject(task?.descrizione, task?.status),
      ),
    );
  }, []);

  const toggleTaskStatus = useCallback((index) => {
    setTasks((prev) =>
      prev.map((task, currentIndex) => {
        if (currentIndex !== index) {
          return createTaskObject(task?.descrizione, task?.status);
        }

        return createTaskObject(
          task?.descrizione,
          task?.status === TASK_STATUS_DONE
            ? TASK_STATUS_OPEN
            : TASK_STATUS_DONE,
        );
      }),
    );
  }, []);

  const removeTask = useCallback((index) => {
    setTasks((prev) =>
      prev.filter((_, currentIndex) => currentIndex !== index),
    );
  }, []);

  const resetTaskPlanner = useCallback((nextTasks = []) => {
    setTasks(normalizeActivityTasks(nextTasks));
    setTaskDraft("");
  }, []);

  return {
    tasks,
    taskDraft,
    setTaskDraft,
    normalizedTasks,
    completedTasksCount,
    addTask,
    updateTask,
    toggleTaskStatus,
    removeTask,
    resetTaskPlanner,
  };
}

function SummaryCards({ cards }) {
  return (
    <div className="plan-summary-grid">
      {cards.map((card) => (
        <div key={card.label} className="plan-summary-card">
          <span>{card.label}</span>
          <strong>{card.value}</strong>
        </div>
      ))}
    </div>
  );
}

function TaskPlannerSection({
  tasks,
  taskDraft,
  setTaskDraft,
  addTask,
  updateTask,
  toggleTaskStatus,
  removeTask,
  completedTasksCount,
  normalizedTasks,
}) {
  return (
    <div className="modal-section-card">
      <div className="plan-section-head">
        <div>
          <h3>Task operativi</h3>
          <p>
            Suddividi l&apos;attività in task tracciabili e definisci già lo
            stato iniziale.
          </p>
        </div>
        <div className="task-counter">
          {completedTasksCount}/{normalizedTasks.length}
        </div>
      </div>

      <div className="task-input-row">
        <input
          type="text"
          value={taskDraft}
          onChange={(e) => setTaskDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTask();
            }
          }}
          placeholder="Es. Sopralluogo, verifica impianto, report finale"
        />
        <button
          type="button"
          className="task-add-btn"
          onClick={() => addTask()}
          disabled={!taskDraft.trim()}
        >
          Aggiungi task
        </button>
      </div>

      {tasks.length > 0 ? (
        <div className="task-list">
          {tasks.map((task, index) => (
            <div key={`sd-task-${index}`} className="task-item-row">
              <span className="task-item-index">{index + 1}</span>
              <label className="task-status-toggle">
                <input
                  type="checkbox"
                  checked={task?.status === TASK_STATUS_DONE}
                  onChange={() => toggleTaskStatus(index)}
                />
                <span>
                  {task?.status === TASK_STATUS_DONE ? "Completato" : "Aperto"}
                </span>
              </label>
              <input
                type="text"
                value={task?.descrizione || ""}
                onChange={(e) => updateTask(index, e.target.value)}
                className={
                  task?.status === TASK_STATUS_DONE ? "task-input-done" : ""
                }
              />
              <button
                type="button"
                className="task-remove-btn"
                onClick={() => removeTask(index)}
              >
                Rimuovi
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="task-empty-state">
          Nessun task inserito. Usa i task per i passaggi operativi e le note
          per il contesto generale.
        </div>
      )}
    </div>
  );
}

export default function SupervisioneDipendenti() {
  const { user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [weeklyAssigned, setWeeklyAssigned] = useState(new Map());
  const [error, setError] = useState(null);
  const { documents: users } = useCollection("users", null, null, null, false);
  const { documents: projects } = useCollection(
    "projects",
    null,
    null,
    null,
    false,
  );
  const { documents: automezzi } = useCollection(
    "automezzi",
    null,
    null,
    null,
    false,
  );

  const [viewMode, setViewMode] = useState("weekly");
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSettore, setSelectedSettore] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [modalProject, setModalProject] = useState(null);
  const [modalMezzo, setModalMezzo] = useState(null);
  const [modalNote, setModalNote] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createUser, setCreateUser] = useState(null);
  const [createDate, setCreateDate] = useState(() => new Date());
  const [createProject, setCreateProject] = useState(null);
  const [createMezzo, setCreateMezzo] = useState(null);
  const [createNote, setCreateNote] = useState("");
  const [taskUpdateError, setTaskUpdateError] = useState("");
  const [isUpdatingTaskKey, setIsUpdatingTaskKey] = useState("");

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState(null);
  const [bulkEndDate, setBulkEndDate] = useState(null);
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);
  const [bulkProject, setBulkProject] = useState(null);
  const [bulkMezzo, setBulkMezzo] = useState(null);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [hiddenUsersOpen, setHiddenUsersOpen] = useState(false);
  const [hiddenUsersQuery, setHiddenUsersQuery] = useState("");
  const [hiddenUserIds, setHiddenUserIds] = useState(() => {
    try {
      const raw = window.localStorage?.getItem("supervisione.hiddenUserIds");
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return new Set();
      return new Set(arr.filter((x) => typeof x === "string"));
    } catch {
      return new Set();
    }
  });

  const [gcalEmbedUrl, setGcalEmbedUrl] = useState(DEFAULT_GCAL_EMBED_URL);
  const [gcalEmbedMode, setGcalEmbedMode] = useState("AGENDA");
  const [collapsedSettori, setCollapsedSettori] = useState(() => new Set());

  const {
    tasks: modalTasks,
    taskDraft: modalTaskDraft,
    setTaskDraft: setModalTaskDraft,
    normalizedTasks: normalizedModalTasks,
    completedTasksCount: completedModalTasksCount,
    addTask: addModalTask,
    updateTask: updateModalTask,
    toggleTaskStatus: toggleModalTaskStatus,
    removeTask: removeModalTask,
    resetTaskPlanner: resetModalTaskPlanner,
  } = useTaskPlannerState();

  const {
    tasks: createTasks,
    taskDraft: createTaskDraft,
    setTaskDraft: setCreateTaskDraft,
    normalizedTasks: normalizedCreateTasks,
    completedTasksCount: completedCreateTasksCount,
    addTask: addCreateTask,
    updateTask: updateCreateTask,
    toggleTaskStatus: toggleCreateTaskStatus,
    removeTask: removeCreateTask,
    resetTaskPlanner: resetCreateTaskPlanner,
  } = useTaskPlannerState();

  const {
    tasks: bulkTasks,
    taskDraft: bulkTaskDraft,
    setTaskDraft: setBulkTaskDraft,
    normalizedTasks: normalizedBulkTasks,
    completedTasksCount: completedBulkTasksCount,
    addTask: addBulkTask,
    updateTask: updateBulkTask,
    toggleTaskStatus: toggleBulkTaskStatus,
    removeTask: removeBulkTask,
    resetTaskPlanner: resetBulkTaskPlanner,
  } = useTaskPlannerState();

  useEffect(() => {
    try {
      window.localStorage?.setItem(
        "supervisione.hiddenUserIds",
        JSON.stringify(Array.from(hiddenUserIds)),
      );
    } catch {}
  }, [hiddenUserIds]);

  useEffect(() => {
    if (!modalOpen && !createOpen && !bulkOpen && !hiddenUsersOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen, createOpen, bulkOpen, hiddenUsersOpen]);

  const gcalIframeSrc = useMemo(() => {
    if (!gcalEmbedUrl || typeof gcalEmbedUrl !== "string") return "";
    try {
      const url = new URL(gcalEmbedUrl.trim());
      if (gcalEmbedMode) url.searchParams.set("mode", gcalEmbedMode);
      url.searchParams.set("showTitle", "0");
      url.searchParams.set("showTabs", "0");
      url.searchParams.set("showCalendars", "0");
      url.searchParams.set("showPrint", "0");
      url.searchParams.set("showTz", "0");
      return url.toString();
    } catch {
      return gcalEmbedUrl.trim();
    }
  }, [gcalEmbedUrl, gcalEmbedMode]);

  const userMap = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      m.set(u.id, getUserDisplayName(u));
    });
    return m;
  }, [users]);

  const userOptions = useMemo(() => {
    const opts = (users || []).map((u) => ({
      value: u.id,
      label: getUserDisplayName(u),
    }));
    return [{ value: "__all__", label: "Tutti" }, ...opts];
  }, [users]);

  const userSelectOptions = useMemo(() => {
    return (users || []).map((u) => ({
      value: u.id,
      label: getUserDisplayName(u),
    }));
  }, [users]);

  const settoreOptions = useMemo(() => {
    const set = new Set();
    (users || []).forEach((u) => {
      const s = u.anagrafica?.settore;
      if (s && typeof s === "string") set.add(s);
    });
    const opts = Array.from(set).map((s) => ({ value: s, label: s }));
    return [{ value: "__all__", label: "Tutti" }, ...opts];
  }, [users]);

  const userSettoreMap = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      m.set(u.id, u.anagrafica?.settore || null);
    });
    return m;
  }, [users]);

  const projectOptions = useMemo(() => {
    return (projects || []).map((p) => ({
      value: p.id,
      label: p.nome || p.codice || p.id,
    }));
  }, [projects]);

  const mezzoOptions = useMemo(() => {
    return (automezzi || []).map((m) => ({
      value: m.id,
      label: m.targa ? `${m.modello || ""} ${m.targa}` : m.modello || m.id,
    }));
  }, [automezzi]);

  const automezziById = useMemo(() => {
    const m = new Map();
    (automezzi || []).forEach((doc) => m.set(doc.id, doc));
    return m;
  }, [automezzi]);

  const weekDays = useMemo(() => {
    if (viewMode !== "weekly") return [];
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate, viewMode]);

  const effectiveSelectedUserId =
    selectedUser && selectedUser.value && selectedUser.value !== "__all__"
      ? selectedUser.value
      : null;
  const effectiveSelectedSettore =
    selectedSettore &&
    selectedSettore.value &&
    selectedSettore.value !== "__all__"
      ? selectedSettore.value
      : null;

  const visibleUsers = useMemo(() => {
    const all = users || [];
    const bySettore = effectiveSelectedSettore
      ? all.filter((u) => u.anagrafica?.settore === effectiveSelectedSettore)
      : all;

    if (effectiveSelectedUserId) {
      return bySettore.filter((u) => u.id === effectiveSelectedUserId);
    }

    return bySettore.filter((u) => !hiddenUserIds.has(u.id));
  }, [users, effectiveSelectedSettore, effectiveSelectedUserId, hiddenUserIds]);

  const weeklyUserGroups = useMemo(() => {
    if (viewMode !== "weekly") return [];
    const map = new Map();
    (visibleUsers || []).forEach((u) => {
      const settoreRaw = u?.anagrafica?.settore;
      const settore =
        typeof settoreRaw === "string" && settoreRaw.trim()
          ? settoreRaw.trim()
          : "Senza settore";
      if (!map.has(settore)) map.set(settore, []);
      map.get(settore).push(u);
    });

    const settoreNames = Array.from(map.keys()).sort((a, b) =>
      String(a).localeCompare(String(b), "it", { sensitivity: "base" }),
    );

    return settoreNames.map((settore) => {
      const groupUsers = map.get(settore) || [];
      groupUsers.sort((a, b) => {
        const la = userMap.get(a.id) || a.displayName || a.id;
        const lb = userMap.get(b.id) || b.displayName || b.id;
        return String(la).localeCompare(String(lb), "it", {
          sensitivity: "base",
        });
      });
      return { settore, users: groupUsers };
    });
  }, [viewMode, visibleUsers, userMap]);

  const dailyActivitiesByUserId = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => {
      if (it?.userId) m.set(it.userId, it);
    });
    return m;
  }, [items]);

  const dailyUserGroups = useMemo(() => {
    if (viewMode !== "daily") return [];
    const map = new Map();
    (visibleUsers || []).forEach((u) => {
      const settoreRaw = u?.anagrafica?.settore;
      const settore =
        typeof settoreRaw === "string" && settoreRaw.trim()
          ? settoreRaw.trim()
          : "Senza settore";
      if (!map.has(settore)) map.set(settore, []);
      map.get(settore).push(u);
    });

    const settoreNames = Array.from(map.keys()).sort((a, b) =>
      String(a).localeCompare(String(b), "it", { sensitivity: "base" }),
    );

    return settoreNames.map((settore) => {
      const groupUsers = map.get(settore) || [];
      groupUsers.sort((a, b) => {
        const la = userMap.get(a.id) || a.displayName || a.id;
        const lb = userMap.get(b.id) || b.displayName || b.id;
        return String(la).localeCompare(String(lb), "it", {
          sensitivity: "base",
        });
      });
      const assignedCount = groupUsers.reduce(
        (acc, u) => acc + (dailyActivitiesByUserId.has(u.id) ? 1 : 0),
        0,
      );
      return { settore, users: groupUsers, assignedCount };
    });
  }, [viewMode, visibleUsers, userMap, dailyActivitiesByUserId]);

  const selectedDateLabel = useMemo(
    () => format(currentDate, "EEEE dd MMMM yyyy", { locale: itLocale }),
    [currentDate],
  );

  const dailyTaskActivities = useMemo(() => {
    if (viewMode !== "daily") return [];

    return (items || [])
      .map((activity) => ({
        activity,
        tasks: normalizeActivityTasks(activity?.tasks),
        userLabel:
          userMap.get(activity?.userId) ||
          users?.find((u) => u.id === activity?.userId)?.displayName ||
          activity?.userId ||
          "—",
        settore:
          userSettoreMap.get(activity?.userId) ||
          users?.find((u) => u.id === activity?.userId)?.anagrafica?.settore ||
          "Senza settore",
      }))
      .filter(
        ({ activity, tasks }) =>
          !isAssignmentAbsence(activity) && tasks.length > 0,
      )
      .sort((a, b) => {
        const settoreCmp = String(a.settore).localeCompare(
          String(b.settore),
          "it",
          {
            sensitivity: "base",
          },
        );
        if (settoreCmp !== 0) return settoreCmp;
        return String(a.userLabel).localeCompare(String(b.userLabel), "it", {
          sensitivity: "base",
        });
      });
  }, [items, userMap, userSettoreMap, users, viewMode]);

  const totalDailyTasksCount = useMemo(
    () => dailyTaskActivities.reduce((acc, row) => acc + row.tasks.length, 0),
    [dailyTaskActivities],
  );

  const completedDailyTasksCount = useMemo(
    () =>
      dailyTaskActivities.reduce(
        (acc, row) =>
          acc +
          row.tasks.filter((task) => task.status === TASK_STATUS_DONE).length,
        0,
      ),
    [dailyTaskActivities],
  );

  const loadDaily = useCallback(
    async (day) => {
      setIsLoading(true);
      setError(null);
      try {
        const dateStr = format(day, "yyyy-MM-dd");
        const dateTs = timestamp.fromDate(new Date(`${dateStr}T00:00:00`));
        let query = database.collection("attivita").where("date", "==", dateTs);
        if (effectiveSelectedUserId)
          query = query.where("userId", "==", effectiveSelectedUserId);
        const qSnap = await query.get();
        let arr = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (effectiveSelectedSettore) {
          arr = arr.filter(
            (it) => userSettoreMap.get(it.userId) === effectiveSelectedSettore,
          );
        }
        if (!effectiveSelectedUserId) {
          arr = arr.filter((it) => !hiddenUserIds.has(it.userId));
        }
        setItems(arr);
        setWeeklyAssigned(new Map());
      } catch (err) {
        setError(err.message);
        setItems([]);
        setWeeklyAssigned(new Map());
      } finally {
        setIsLoading(false);
      }
    },
    [
      effectiveSelectedSettore,
      effectiveSelectedUserId,
      hiddenUserIds,
      userSettoreMap,
    ],
  );

  const loadWeekly = useCallback(
    async (anchorDate) => {
      setIsLoading(true);
      setError(null);
      try {
        const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
        const end = endOfWeek(anchorDate, { weekStartsOn: 1 });

        const startStr = format(start, "yyyy-MM-dd");
        const endStr = format(end, "yyyy-MM-dd");
        const startTs = timestamp.fromDate(new Date(`${startStr}T00:00:00`));
        const endTs = timestamp.fromDate(new Date(`${endStr}T00:00:00`));

        let query = database
          .collection("attivita")
          .where("date", ">=", startTs)
          .where("date", "<=", endTs);

        if (effectiveSelectedUserId)
          query = query.where("userId", "==", effectiveSelectedUserId);
        const qSnap = await query.get();
        let arr = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (effectiveSelectedSettore) {
          arr = arr.filter(
            (it) => userSettoreMap.get(it.userId) === effectiveSelectedSettore,
          );
        }
        if (!effectiveSelectedUserId) {
          arr = arr.filter((it) => !hiddenUserIds.has(it.userId));
        }

        const next = new Map();
        arr.forEach((it) => {
          const dateStr = it.date?.toDate
            ? format(it.date.toDate(), "yyyy-MM-dd")
            : it.dateStr || null;
          if (!dateStr) return;
          if (!next.has(it.userId)) next.set(it.userId, new Map());
          next.get(it.userId).set(dateStr, it);
        });

        setWeeklyAssigned(next);
        setItems([]);
      } catch (err) {
        setError(err.message);
        setWeeklyAssigned(new Map());
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      effectiveSelectedSettore,
      effectiveSelectedUserId,
      hiddenUserIds,
      userSettoreMap,
    ],
  );

  const refresh = useCallback(async () => {
    if (viewMode === "weekly") return loadWeekly(currentDate);
    return loadDaily(currentDate);
  }, [currentDate, loadDaily, loadWeekly, viewMode]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    currentDate,
    effectiveSelectedUserId,
    effectiveSelectedSettore,
    hiddenUserIds,
  ]);

  const openEditModal = (it) => {
    setModalItem(it);
    const initialProject = it.project?.id
      ? projectOptions.find((o) => o.value === it.project.id)
      : null;
    const initialMezzo = it.mezzo?.id
      ? mezzoOptions.find((o) => o.value === it.mezzo.id)
      : null;
    setModalProject(initialProject || null);
    setModalMezzo(initialMezzo || null);
    setModalNote(it.note || "");
    resetModalTaskPlanner(it.tasks);
    setModalOpen(true);
  };

  const confirmEdit = async () => {
    try {
      const updates = {};
      if (modalProject && modalProject.value) {
        const p =
          (projects || []).find((pr) => pr.id === modalProject.value) || {};
        updates.project = {
          id: modalProject.value,
          nome: p.nome,
          codice: p.codice,
        };
      }
      if (modalMezzo !== undefined) {
        if (modalMezzo && modalMezzo.value) {
          const m =
            (automezzi || []).find((mz) => mz.id === modalMezzo.value) || {};
          updates.mezzo = {
            id: modalMezzo.value,
            modello: m.modello,
            targa: m.targa,
          };
        } else {
          updates.mezzo = null;
        }
      }
      updates.note = sanitizeOptionalText(modalNote);
      updates.tasks = normalizedModalTasks;
      updates.updatedBy = user?.uid || "";
      updates.updatedAt = timestamp.fromDate(new Date());
      if (!modalItem) return;
      await database.collection("attivita").doc(modalItem.id).update(updates);
      setModalOpen(false);
      setModalItem(null);
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const openCreateModal = (userId, date) => {
    const presetUser =
      userId && (users || []).some((u) => u.id === userId)
        ? userSelectOptions.find((o) => o.value === userId)
        : null;
    const presetDate = date instanceof Date ? date : null;
    setCreateUser(
      presetUser ||
        (selectedUser && selectedUser.value !== "__all__"
          ? selectedUser
          : null),
    );
    setCreateDate(presetDate || currentDate || new Date());
    setCreateProject(null);
    setCreateMezzo(null);
    setCreateNote("");
    resetCreateTaskPlanner();
    setCreateOpen(true);
  };

  const confirmCreate = async () => {
    try {
      if (!createUser || !createUser.value) return;
      if (!createProject || !createProject.value) return;
      if (!createDate) return;
      const dateStr = format(createDate, "yyyy-MM-dd");
      const p =
        (projects || []).find((pr) => pr.id === createProject.value) || {};
      const payload = {
        userId: createUser.value,
        project: {
          id: createProject.value,
          nome: p.nome,
          codice: p.codice,
        },
        mezzo: null,
        note: sanitizeOptionalText(createNote),
        tasks: normalizedCreateTasks,
        status: "pianificata",
        date: timestamp.fromDate(new Date(`${dateStr}T00:00:00`)),
        createdAt: timestamp.fromDate(new Date()),
        updatedAt: timestamp.fromDate(new Date()),
        createdBy: user?.uid || "",
        updatedBy: user?.uid || "",
      };
      if (createMezzo && createMezzo.value) {
        const m =
          (automezzi || []).find((mz) => mz.id === createMezzo.value) || {};
        payload.mezzo = {
          id: createMezzo.value,
          modello: m.modello,
          targa: m.targa,
        };
      }
      const docId = `${createUser.value}_${dateStr}`;
      await database.collection("attivita").doc(docId).set(payload);
      setCreateOpen(false);
      setCurrentDate(createDate);
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const bulkUserOptions = useMemo(() => {
    return (visibleUsers || []).map((u) => ({
      value: u.id,
      label: userMap.get(u.id) || u.displayName || u.id,
    }));
  }, [visibleUsers, userMap]);

  const openBulkModal = () => {
    setBulkResult(null);
    setBulkStartDate(currentDate || new Date());
    setBulkEndDate(null);
    setBulkProject(null);
    setBulkMezzo(null);
    setBulkNote("");
    resetBulkTaskPlanner();
    setBulkSelectedUsers(effectiveSelectedUserId ? bulkUserOptions : []);
    setBulkOpen(true);
  };

  const confirmBulkCreate = async () => {
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      if (!user?.uid) throw new Error("Utente non autenticato");
      const usersSel = bulkSelectedUsers || [];
      const hasUsers = usersSel.length > 0;
      const hasProject = !!(bulkProject && bulkProject.value);
      const hasMezzo = !!(bulkMezzo && bulkMezzo.value);
      const hasNote = !!bulkNote.trim();
      const hasTasks = normalizedBulkTasks.length > 0;
      if (!hasUsers || !(hasProject || hasMezzo || hasNote || hasTasks)) {
        throw new Error(
          "Seleziona almeno un dipendente e almeno uno tra cantiere, mezzo, note o task",
        );
      }

      const startD = bulkStartDate || currentDate || new Date();
      const endD = bulkEndDate && bulkEndDate >= startD ? bulkEndDate : startD;
      const days = eachDayOfInterval({ start: startD, end: endD });
      const nowTs = timestamp.fromDate(new Date());

      const proj = hasProject
        ? (projects || []).find((p) => p.id === bulkProject.value) || null
        : null;
      const mezzoDoc = hasMezzo
        ? automezziById.get(bulkMezzo.value) || null
        : null;

      for (const day of days) {
        const dateStr = format(day, "yyyy-MM-dd");
        const dayStart = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          0,
          0,
          0,
          0,
        );
        const dateTs = timestamp.fromDate(dayStart);

        for (const uOpt of usersSel) {
          const docId = `${uOpt.value}_${dateStr}`;
          const attRef = database.collection("attivita").doc(docId);
          const existing = await attRef.get();

          const payload = {
            userId: uOpt.value,
            date: dateTs,
            updatedBy: user.uid,
            updatedAt: nowTs,
          };
          if (!existing.exists) {
            payload.createdBy = user.uid;
            payload.createdAt = nowTs;
          }
          if (hasProject) {
            payload.project = {
              id: bulkProject.value,
              nome: proj?.nome,
              codice: proj?.codice,
            };
            payload.status = "pianificata";
          }
          if (hasMezzo) {
            payload.mezzo = {
              id: bulkMezzo.value,
              modello: mezzoDoc?.modello,
              targa: mezzoDoc?.targa,
            };
            payload.status = "pianificata";
          }
          if (hasNote) {
            payload.note = sanitizeOptionalText(bulkNote);
            payload.status = "pianificata";
          }
          if (hasTasks) {
            payload.tasks = normalizedBulkTasks;
            payload.status = "pianificata";
          }
          await attRef.set(payload, { merge: true });
        }
      }

      setBulkResult({ ok: true });
      setBulkOpen(false);
      await refresh();
    } catch (e) {
      setBulkResult({ ok: false, error: e.message });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("modal-overlay")) {
      setModalOpen(false);
      setCreateOpen(false);
      setBulkOpen(false);
      setHiddenUsersOpen(false);
    }
  };

  const visibleUsersForToggles = useMemo(() => {
    const q = hiddenUsersQuery.trim().toLowerCase();
    const all = users || [];
    if (!q) return all;
    return all.filter((u) => {
      const label = userMap.get(u.id) || u.displayName || u.id;
      return String(label).toLowerCase().includes(q);
    });
  }, [users, hiddenUsersQuery, userMap]);

  const handleToggleDayTask = useCallback(
    async (activityId, taskIndex) => {
      if (!activityId || isUpdatingTaskKey) return;
      const activity = (items || []).find((row) => row.id === activityId);
      if (!activity) return;

      const nextTasks = normalizeActivityTasks(activity.tasks).map(
        (task, index) =>
          index === taskIndex
            ? createTaskObject(
                task.descrizione,
                task.status === TASK_STATUS_DONE
                  ? TASK_STATUS_OPEN
                  : TASK_STATUS_DONE,
              )
            : createTaskObject(task.descrizione, task.status),
      );

      setTaskUpdateError("");
      setIsUpdatingTaskKey(`${activityId}_${taskIndex}`);

      try {
        await database
          .collection("attivita")
          .doc(activityId)
          .set(
            {
              tasks: nextTasks,
              updatedAt: timestamp.fromDate(new Date()),
              updatedBy: user?.uid || "",
            },
            { merge: true },
          );
        await refresh();
      } catch (toggleError) {
        setTaskUpdateError(
          toggleError?.message ||
            "Impossibile aggiornare il task in questo momento.",
        );
      } finally {
        setIsUpdatingTaskKey("");
      }
    },
    [isUpdatingTaskKey, items, refresh, user?.uid],
  );

  return (
    <AdminOnly>
      <div className="supervisione-container">
        <h1>Supervisione Dipendenti</h1>
        <div className="supervisione-header">
          <div className="supervisione-view">
            <button
              type="button"
              className={`view-btn ${viewMode === "daily" ? "active" : ""}`}
              onClick={() => setViewMode("daily")}
            >
              Giornaliera
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === "weekly" ? "active" : ""}`}
              onClick={() => setViewMode("weekly")}
            >
              Settimanale
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === "calendar" ? "active" : ""}`}
              onClick={() => setViewMode("calendar")}
            >
              Calendario
            </button>
          </div>
          <div className="supervisione-date">
            <div className="react-select-like-control">
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={itLocale}
              >
                <DatePicker
                  value={currentDate}
                  onChange={(d) => d && setCurrentDate(d)}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                        },
                      },
                    },
                  }}
                  clearable
                />
              </LocalizationProvider>
            </div>
            <div className="dash-stepper">
              <button
                type="button"
                className="dash-stepper-btn"
                onClick={() =>
                  setCurrentDate(
                    viewMode === "daily"
                      ? addDays(currentDate, -1)
                      : addDays(currentDate, -7),
                  )
                }
              >
                −
              </button>
              <button
                type="button"
                className="dash-stepper-btn"
                onClick={() =>
                  setCurrentDate(
                    viewMode === "daily"
                      ? addDays(currentDate, 1)
                      : addDays(currentDate, 7),
                  )
                }
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div className="controls">
          <div className="control-row">
            <label>Dipendente</label>
            <div className="react-select-like-control">
              <Select
                className="select-user"
                classNamePrefix="react-select"
                value={selectedUser}
                onChange={(opt) => setSelectedUser(opt)}
                options={userOptions}
                placeholder="Filtra per utente"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_MENU_PORTAL_STYLES}
              />
            </div>
          </div>
          <div className="control-row">
            <label>Settore</label>
            <div className="react-select-like-control">
              <Select
                className="select-user"
                classNamePrefix="react-select"
                value={selectedSettore}
                onChange={(opt) => setSelectedSettore(opt)}
                options={settoreOptions}
                placeholder="Filtra per settore"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_MENU_PORTAL_STYLES}
              />
            </div>
          </div>
          <div className="control-row">
            <label>Utenti</label>
            <button
              type="button"
              className="load-btn secondary"
              onClick={() => setHiddenUsersOpen((v) => !v)}
            >
              Nascondi utenti
            </button>
            {hiddenUsersOpen && (
              <div className="hidden-users-panel">
                <div className="hidden-users-head">
                  <input
                    type="text"
                    value={hiddenUsersQuery}
                    onChange={(e) => setHiddenUsersQuery(e.target.value)}
                    className="react-select-like-input"
                    placeholder="Cerca dipendente..."
                  />
                  <button
                    type="button"
                    className="load-btn small"
                    onClick={() => setHiddenUserIds(new Set())}
                  >
                    Mostra tutti
                  </button>
                </div>
                <div className="hidden-users-list">
                  {visibleUsersForToggles.map((u) => {
                    const checked = !hiddenUserIds.has(u.id);
                    const label = userMap.get(u.id) || u.displayName || u.id;
                    return (
                      <label key={u.id} className="toggle-row">
                        <span className="toggle-label">{label}</span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setHiddenUserIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(u.id)) next.delete(u.id);
                              else next.add(u.id);
                              return next;
                            })
                          }
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="control-row">
            <label style={{ visibility: "hidden" }}>Azioni</label>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                type="button"
                className="load-btn secondary"
                onClick={openBulkModal}
              >
                Pianifica massivamente
              </button>
              <button
                type="button"
                className="load-btn"
                onClick={() => openCreateModal()}
              >
                Nuova Attività
              </button>
            </div>
          </div>
        </div>

        {error && <div className="error-box">Errore: {error}</div>}

        {isLoading ? (
          <div className="table-empty">Caricamento...</div>
        ) : viewMode === "daily" ? (
          <div className="daily-layout">
            <div className="task-verification-summary">
              <SummaryCards
                cards={[
                  { label: "Data", value: selectedDateLabel },
                  {
                    label: "Attività con task",
                    value: String(dailyTaskActivities.length),
                  },
                  {
                    label: "Task completati",
                    value: `${completedDailyTasksCount}/${totalDailyTasksCount}`,
                  },
                  {
                    label: "Dipendenti visibili",
                    value: String(visibleUsers.length),
                  },
                ]}
              />
            </div>
            {dailyUserGroups.length === 0 ? (
              <div className="table-empty">Nessun dipendente</div>
            ) : (
              <div className="daily-sections">
                {dailyUserGroups.map((g) => (
                  <section key={g.settore} className="daily-sector">
                    <div className="daily-settore-sep">
                      <button
                        type="button"
                        className="weekly-settore-toggle"
                        onClick={() => {
                          setCollapsedSettori((prev) => {
                            const next = new Set(prev);
                            if (next.has(g.settore)) next.delete(g.settore);
                            else next.add(g.settore);
                            return next;
                          });
                        }}
                      >
                        <span className="weekly-settore-arrow">
                          {collapsedSettori.has(g.settore) ? "▸" : "▾"}
                        </span>
                        <span className="weekly-settore-name">{g.settore}</span>
                        <span className="weekly-settore-count">
                          {g.assignedCount}/{g.users.length} assegnati
                        </span>
                      </button>
                    </div>
                    {!collapsedSettori.has(g.settore) && (
                      <div className="daily-cards">
                        {g.users.map((u) => {
                          const it = dailyActivitiesByUserId.get(u.id) || null;
                          const nome =
                            userMap.get(u.id) || u.displayName || u.id;
                          const cardKey = it?.id || `empty_${u.id}`;
                          const statusLabel = it?.status || "Non assegnato";
                          return (
                            <div
                              key={cardKey}
                              className={`daily-card ${
                                it ? "is-assigned" : "is-empty"
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                if (it) openEditModal(it);
                                else openCreateModal(u.id, currentDate);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  if (it) openEditModal(it);
                                  else openCreateModal(u.id, currentDate);
                                }
                              }}
                            >
                              <div className="daily-card-top">
                                <div className="daily-card-name">{nome}</div>
                                <div
                                  className={`daily-badge ${
                                    it
                                      ? isAssignmentAbsence(it)
                                        ? "assenza"
                                        : "assigned"
                                      : "empty"
                                  }`}
                                >
                                  {it
                                    ? isAssignmentAbsence(it)
                                      ? "Assenza"
                                      : statusLabel
                                    : statusLabel}
                                </div>
                              </div>
                              <div className="daily-card-subtitle">
                                {selectedDateLabel}
                              </div>
                              <div className="daily-card-grid">
                                <div className="daily-field">
                                  <div className="daily-field-label">
                                    Cantiere
                                  </div>
                                  <div className="daily-field-value">
                                    {isAssignmentAbsence(it)
                                      ? "Assenza"
                                      : getProjectDisplayLabel(it?.project)}
                                  </div>
                                </div>
                                <div className="daily-field">
                                  <div className="daily-field-label">Mezzo</div>
                                  <div className="daily-field-value">
                                    {getVehicleDisplayLabel(it?.mezzo)}
                                  </div>
                                </div>
                                <div className="daily-field">
                                  <div className="daily-field-label">Task</div>
                                  <div className="daily-field-value">
                                    {it
                                      ? `${
                                          normalizeActivityTasks(
                                            it?.tasks,
                                          ).filter(
                                            (task) =>
                                              task.status === TASK_STATUS_DONE,
                                          ).length
                                        }/${
                                          normalizeActivityTasks(it?.tasks)
                                            .length
                                        }`
                                      : "0/0"}
                                  </div>
                                </div>
                                <div className="daily-field">
                                  <div className="daily-field-label">Note</div>
                                  <div className="daily-field-note">
                                    {it?.note || "—"}
                                  </div>
                                </div>
                              </div>
                              <div className="daily-card-actions">
                                {it ? (
                                  <>
                                    <div className="daily-card-task-inline">
                                      <strong>Task:</strong>{" "}
                                      {formatTasksInline(it?.tasks) ||
                                        "Nessuno"}
                                    </div>
                                    <button
                                      type="button"
                                      className="load-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditModal(it);
                                      }}
                                    >
                                      Modifica
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    className="load-btn secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCreateModal(u.id, currentDate);
                                    }}
                                  >
                                    Crea attività
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}
            <section className="task-verification-panel">
              <div className="task-verification-head">
                <div>
                  <h3>Verifica Task Del Giorno</h3>
                  <p>
                    Controlla e aggiorna i task delle attività del giorno per i
                    dipendenti attualmente filtrati.
                  </p>
                </div>
                <div className="task-counter">
                  {completedDailyTasksCount}/{totalDailyTasksCount}
                </div>
              </div>
              {taskUpdateError && (
                <div className="error-box" style={{ marginBottom: 12 }}>
                  {taskUpdateError}
                </div>
              )}
              {dailyTaskActivities.length === 0 ? (
                <div className="task-empty-state">
                  Nessun task pianificato per la data selezionata.
                </div>
              ) : (
                <div className="task-verification-list">
                  {dailyTaskActivities.map(
                    ({ activity, tasks, userLabel, settore }) => (
                      <div key={activity.id} className="task-verification-card">
                        <div className="task-verification-card-top">
                          <div>
                            <div className="task-verification-title">
                              {userLabel}
                            </div>
                            <div className="task-verification-meta">
                              {settore} ·{" "}
                              {getProjectDisplayLabel(activity?.project)}
                            </div>
                          </div>
                          <div className="task-counter">
                            {
                              tasks.filter(
                                (task) => task.status === TASK_STATUS_DONE,
                              ).length
                            }
                            /{tasks.length}
                          </div>
                        </div>
                        <div className="task-verification-items">
                          {tasks.map((task, index) => {
                            const toggleKey = `${activity.id}_${index}`;
                            const isUpdating = isUpdatingTaskKey === toggleKey;
                            const isDone = task.status === TASK_STATUS_DONE;

                            return (
                              <label
                                key={toggleKey}
                                className={`task-verification-item ${
                                  isDone ? "done" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isDone}
                                  disabled={Boolean(isUpdatingTaskKey)}
                                  onChange={() =>
                                    handleToggleDayTask(activity.id, index)
                                  }
                                />
                                <span className="task-verification-item-text">
                                  {task.descrizione}
                                </span>
                                <span className="task-verification-item-status">
                                  {isUpdating
                                    ? "Aggiornamento..."
                                    : isDone
                                      ? "Completato"
                                      : "Aperto"}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </section>
          </div>
        ) : viewMode === "calendar" ? (
          <div className="gcal-embed-panel">
            <div className="gcal-embed-controls">
              <div className="control-row">
                <label>Google Calendar</label>
                <input
                  type="text"
                  value={gcalEmbedUrl}
                  onChange={(e) => setGcalEmbedUrl(e.target.value)}
                  className="react-select-like-input"
                  placeholder="Link embed Google Calendar pubblico"
                />
              </div>
              <div className="gcal-embed-modes">
                <button
                  type="button"
                  className={`view-btn ${
                    gcalEmbedMode === "AGENDA" ? "active" : ""
                  }`}
                  onClick={() => setGcalEmbedMode("AGENDA")}
                >
                  Agenda
                </button>
                <button
                  type="button"
                  className={`view-btn ${
                    gcalEmbedMode === "WEEK" ? "active" : ""
                  }`}
                  onClick={() => setGcalEmbedMode("WEEK")}
                >
                  Settimana
                </button>
              </div>
            </div>
            <iframe
              title="Google Calendar"
              className="gcal-iframe"
              src={gcalIframeSrc}
              frameBorder="0"
            />
          </div>
        ) : (
          <div className="weekly-grid-container">
            <table className="weekly-table">
              <thead>
                <tr>
                  <th className="sticky-col">Dipendente</th>
                  {weekDays.map((d) => (
                    <th
                      key={d.toISOString()}
                      className={isSameDay(d, new Date()) ? "today-col" : ""}
                    >
                      {format(d, "EEE dd/MM", { locale: itLocale })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeklyUserGroups.map((g) => (
                  <React.Fragment key={g.settore}>
                    <tr className="weekly-settore-sep">
                      <td colSpan={weekDays.length + 1}>
                        <button
                          type="button"
                          className="weekly-settore-toggle"
                          onClick={() => {
                            setCollapsedSettori((prev) => {
                              const next = new Set(prev);
                              if (next.has(g.settore)) next.delete(g.settore);
                              else next.add(g.settore);
                              return next;
                            });
                          }}
                        >
                          <span className="weekly-settore-arrow">
                            {collapsedSettori.has(g.settore) ? "▸" : "▾"}
                          </span>
                          <span className="weekly-settore-name">
                            {g.settore}
                          </span>
                          <span className="weekly-settore-count">
                            {g.users.length}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {!collapsedSettori.has(g.settore) &&
                      g.users.map((u) => {
                        const nome = userMap.get(u.id) || u.displayName || u.id;
                        const userAssigns = weeklyAssigned.get(u.id);
                        return (
                          <tr key={u.id}>
                            <td className="sticky-col user-cell">{nome}</td>
                            {weekDays.map((d) => {
                              const dateStr = format(d, "yyyy-MM-dd");
                              const ass = userAssigns?.get(dateStr);
                              const isAssenzaStatus =
                                ass?.status === "ok" ||
                                ass?.status === "assenza";

                              return (
                                <td
                                  key={`${u.id}_${dateStr}`}
                                  className={`sd-day-cell ${
                                    ass
                                      ? isAssenzaStatus
                                        ? "cell-assenza"
                                        : "cell-assigned"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (ass) openEditModal(ass);
                                    else openCreateModal(u.id, d);
                                  }}
                                >
                                  <div className="sd-cell">
                                    {ass ? (
                                      isAssenzaStatus ? (
                                        <div className="sd-assignment">
                                          Assenza
                                        </div>
                                      ) : (
                                        <div className="sd-assignment">
                                          <div className="sd-proj">
                                            {ass.project?.nome || "—"}
                                          </div>
                                          <div className="sd-meta">
                                            {[
                                              ass.project?.codice,
                                              ass.mezzo?.targa ||
                                                ass.mezzo?.modello,
                                            ]
                                              .filter(Boolean)
                                              .join(" · ")}
                                          </div>
                                        </div>
                                      )
                                    ) : (
                                      <div className="sd-empty">+</div>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {modalOpen && (
          <div
            className="modal-overlay sd-modal-overlay"
            onClick={handleOverlayClick}
          >
            <div className="modal-content sd-modal-content sd-modal-content-plan">
              <div className="sd-modal-header">
                <h2>Modifica Attività</h2>
                <button
                  type="button"
                  className="sd-close-btn"
                  onClick={() => setModalOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="sd-modal-body sd-plan-body">
                <div className="plan-hero">
                  <div>
                    <div className="plan-kicker">Attività esistente</div>
                    <div className="plan-title">
                      {userMap.get(modalItem?.userId) ||
                        modalItem?.userId ||
                        "—"}
                    </div>
                    <div className="plan-subtitle">{selectedDateLabel}</div>
                  </div>
                </div>

                <SummaryCards
                  cards={[
                    {
                      label: "Cantiere",
                      value: getProjectDisplayLabel(modalItem?.project),
                    },
                    {
                      label: "Mezzo",
                      value: getVehicleDisplayLabel(modalItem?.mezzo),
                    },
                    {
                      label: "Task",
                      value: `${completedModalTasksCount}/${normalizedModalTasks.length}`,
                    },
                    {
                      label: "Stato",
                      value: modalItem?.status || "pianificata",
                    },
                  ]}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Dettagli attività</h3>
                      <p>Modifica risorse, note e task della pianificazione.</p>
                    </div>
                  </div>
                  <div className="modal-section-grid modal-section-grid-plan">
                    <div className="control-row">
                      <label>Cantiere</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={modalProject}
                          onChange={(opt) => setModalProject(opt)}
                          options={projectOptions}
                          placeholder="Seleziona cantiere"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Mezzo</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={modalMezzo}
                          onChange={(opt) => setModalMezzo(opt)}
                          options={mezzoOptions}
                          placeholder="Seleziona mezzo"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <TaskPlannerSection
                  tasks={modalTasks}
                  taskDraft={modalTaskDraft}
                  setTaskDraft={setModalTaskDraft}
                  addTask={addModalTask}
                  updateTask={updateModalTask}
                  toggleTaskStatus={toggleModalTaskStatus}
                  removeTask={removeModalTask}
                  completedTasksCount={completedModalTasksCount}
                  normalizedTasks={normalizedModalTasks}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Note attività</h3>
                      <p>Indicazioni operative, accessi e dettagli utili.</p>
                    </div>
                  </div>
                  <div className="control-row">
                    <label>Note</label>
                    <textarea
                      value={modalNote}
                      onChange={(e) => setModalNote(e.target.value)}
                      rows={4}
                      className="sd-textarea"
                      placeholder="Modifica nota"
                    />
                  </div>
                </div>
              </div>
              <div className="sd-modal-footer">
                <button
                  type="button"
                  className="chiudi-modal-btn"
                  onClick={() => setModalOpen(false)}
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  className="conferma-modal-btn"
                  onClick={confirmEdit}
                >
                  Conferma
                </button>
              </div>
            </div>
          </div>
        )}
        {createOpen && (
          <div
            className="modal-overlay sd-modal-overlay"
            onClick={handleOverlayClick}
          >
            <div className="modal-content sd-modal-content sd-modal-content-plan">
              <div className="sd-modal-header">
                <h2>Nuova Attività</h2>
                <button
                  type="button"
                  className="sd-close-btn"
                  onClick={() => setCreateOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="sd-modal-body sd-plan-body">
                <div className="plan-hero">
                  <div>
                    <div className="plan-kicker">Nuova attività</div>
                    <div className="plan-title">
                      {createUser?.label || "Seleziona dipendente"}
                    </div>
                    <div className="plan-subtitle">
                      {createDate
                        ? format(createDate, "EEEE dd MMMM yyyy", {
                            locale: itLocale,
                          })
                        : "Seleziona una data"}
                    </div>
                  </div>
                </div>

                <SummaryCards
                  cards={[
                    {
                      label: "Cantiere",
                      value: createProject?.label || "Nessun cantiere",
                    },
                    {
                      label: "Mezzo",
                      value: createMezzo?.label || "Nessun mezzo",
                    },
                    {
                      label: "Task",
                      value: `${completedCreateTasksCount}/${normalizedCreateTasks.length}`,
                    },
                    { label: "Stato", value: "Attività pianificata" },
                  ]}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Dettagli attività</h3>
                      <p>Seleziona dipendente, data e risorse operative.</p>
                    </div>
                  </div>
                  <div className="modal-section-grid modal-section-grid-plan">
                    <div className="control-row">
                      <label>Dipendente</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={createUser}
                          onChange={(opt) => setCreateUser(opt)}
                          options={userSelectOptions}
                          placeholder="Seleziona dipendente"
                          isClearable={false}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Data</label>
                      <div className="react-select-like-control">
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={itLocale}
                        >
                          <DatePicker
                            value={createDate}
                            onChange={(d) => {
                              if (!d) return;
                              setCreateDate(d);
                            }}
                            format="yyyy-MM-dd"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "6px",
                                  },
                                },
                                placeholder: "Seleziona data (yyyy-MM-dd)",
                              },
                            }}
                            clearable
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Cantiere</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={createProject}
                          onChange={(opt) => setCreateProject(opt)}
                          options={projectOptions}
                          placeholder="Seleziona cantiere"
                          isClearable={false}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Mezzo</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={createMezzo}
                          onChange={(opt) => setCreateMezzo(opt)}
                          options={mezzoOptions}
                          placeholder="Seleziona mezzo"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <TaskPlannerSection
                  tasks={createTasks}
                  taskDraft={createTaskDraft}
                  setTaskDraft={setCreateTaskDraft}
                  addTask={addCreateTask}
                  updateTask={updateCreateTask}
                  toggleTaskStatus={toggleCreateTaskStatus}
                  removeTask={removeCreateTask}
                  completedTasksCount={completedCreateTasksCount}
                  normalizedTasks={normalizedCreateTasks}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Note attività</h3>
                      <p>Inserisci dettagli utili e indicazioni operative.</p>
                    </div>
                  </div>
                  <div className="control-row">
                    <label>Note</label>
                    <textarea
                      value={createNote}
                      onChange={(e) => setCreateNote(e.target.value)}
                      rows={4}
                      className="sd-textarea"
                      placeholder="Inserisci nota"
                    />
                  </div>
                </div>
              </div>
              <div className="sd-modal-footer">
                <button
                  type="button"
                  className="chiudi-modal-btn"
                  onClick={() => setCreateOpen(false)}
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  className="conferma-modal-btn"
                  onClick={confirmCreate}
                  disabled={!createUser || !createProject || !createDate}
                >
                  Conferma
                </button>
              </div>
            </div>
          </div>
        )}
        {bulkOpen && (
          <div
            className="modal-overlay sd-modal-overlay"
            onClick={handleOverlayClick}
          >
            <div className="modal-content sd-modal-content sd-modal-content-plan">
              <div className="sd-modal-header">
                <h2>Pianifica Massivamente</h2>
                <button
                  type="button"
                  className="sd-close-btn"
                  onClick={() => setBulkOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="sd-modal-body sd-plan-body">
                <div className="plan-hero">
                  <div>
                    <div className="plan-kicker">Pianificazione multipla</div>
                    <div className="plan-title">
                      {bulkSelectedUsers.length > 0
                        ? `${bulkSelectedUsers.length} dipendenti selezionati`
                        : "Seleziona i dipendenti"}
                    </div>
                    <div className="plan-subtitle">
                      {bulkStartDate
                        ? bulkEndDate && bulkEndDate >= bulkStartDate
                          ? `${format(bulkStartDate, "dd/MM/yyyy")} - ${format(
                              bulkEndDate,
                              "dd/MM/yyyy",
                            )}`
                          : format(bulkStartDate, "dd/MM/yyyy")
                        : "Seleziona un intervallo"}
                    </div>
                  </div>
                </div>

                <SummaryCards
                  cards={[
                    {
                      label: "Dipendenti",
                      value: String(bulkSelectedUsers.length),
                    },
                    {
                      label: "Cantiere",
                      value: bulkProject?.label || "Nessun cantiere",
                    },
                    {
                      label: "Mezzo",
                      value: bulkMezzo?.label || "Nessun mezzo",
                    },
                    {
                      label: "Task",
                      value: `${completedBulkTasksCount}/${normalizedBulkTasks.length}`,
                    },
                  ]}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Dettagli attività</h3>
                      <p>
                        Applica la stessa pianificazione a più dipendenti e a
                        più giorni.
                      </p>
                    </div>
                  </div>
                  <div className="modal-section-grid modal-section-grid-plan">
                    <div className="control-row">
                      <label>Data inizio</label>
                      <div className="react-select-like-control">
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={itLocale}
                        >
                          <DatePicker
                            value={bulkStartDate}
                            onChange={(d) => setBulkStartDate(d || null)}
                            format="yyyy-MM-dd"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "6px",
                                  },
                                },
                                placeholder:
                                  "Seleziona data inizio (yyyy-MM-dd)",
                              },
                            }}
                            clearable
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Data fine</label>
                      <div className="react-select-like-control">
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={itLocale}
                        >
                          <DatePicker
                            value={bulkEndDate}
                            onChange={(d) => setBulkEndDate(d || null)}
                            format="yyyy-MM-dd"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "6px",
                                  },
                                },
                                placeholder: "Seleziona data fine (yyyy-MM-dd)",
                              },
                            }}
                            clearable
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Dipendenti</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={bulkSelectedUsers}
                          onChange={(opts) => setBulkSelectedUsers(opts || [])}
                          options={bulkUserOptions}
                          isMulti
                          isSearchable
                          placeholder="Seleziona uno o più dipendenti"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Cantiere</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={bulkProject}
                          onChange={(opt) => setBulkProject(opt)}
                          options={projectOptions}
                          isSearchable
                          isClearable
                          placeholder="Seleziona cantiere (opzionale)"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                    <div className="control-row">
                      <label>Mezzo</label>
                      <div className="react-select-like-control">
                        <Select
                          className="select-user"
                          classNamePrefix="react-select"
                          value={bulkMezzo}
                          onChange={(opt) => setBulkMezzo(opt)}
                          options={mezzoOptions}
                          isSearchable
                          isClearable
                          placeholder="Seleziona mezzo (opzionale)"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={SELECT_MENU_PORTAL_STYLES}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <TaskPlannerSection
                  tasks={bulkTasks}
                  taskDraft={bulkTaskDraft}
                  setTaskDraft={setBulkTaskDraft}
                  addTask={addBulkTask}
                  updateTask={updateBulkTask}
                  toggleTaskStatus={toggleBulkTaskStatus}
                  removeTask={removeBulkTask}
                  completedTasksCount={completedBulkTasksCount}
                  normalizedTasks={normalizedBulkTasks}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Note attività</h3>
                      <p>
                        Inserisci dettagli utili, vincoli o indicazioni
                        operative da applicare a tutti.
                      </p>
                    </div>
                  </div>
                  <div className="control-row">
                    <label>Note</label>
                    <textarea
                      value={bulkNote}
                      onChange={(e) => setBulkNote(e.target.value)}
                      rows={3}
                      className="sd-textarea"
                      placeholder="Note (opzionale)"
                    />
                  </div>
                </div>
                {bulkResult && (
                  <div
                    className={bulkResult.ok ? "success-box" : "error-box"}
                    style={{ marginTop: 12 }}
                  >
                    {bulkResult.ok
                      ? "Pianificazione massiva completata"
                      : `Errore: ${bulkResult.error}`}
                  </div>
                )}
              </div>
              <div className="sd-modal-footer">
                <button
                  type="button"
                  className="chiudi-modal-btn"
                  onClick={() => setBulkOpen(false)}
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  className="conferma-modal-btn"
                  onClick={confirmBulkCreate}
                  disabled={bulkSubmitting}
                >
                  {bulkSubmitting ? "Salvataggio..." : "Conferma"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminOnly>
  );
}
